/**
 * Shared ticket reference styling utilities.
 *
 * Provides consistent visual treatment for ticket references across:
 * - Ticket table (summary column)
 * - Autocomplete/typeahead dropdown
 * - Editor inline ticket nodes
 *
 * Design rationale:
 * - Open tickets: Red color matches industry convention (GitHub, Jira, etc.)
 * - Closed tickets: Green color indicates resolved/completed state
 * - Mono font for ticket numbers aids scanning
 * - Strikethrough on closed titles reinforces resolved state
 */

import { LINE_THROUGH_CLASSES } from './styles';
import { cn } from './utils';

export type TicketStatus = 'opened' | 'closed';

/**
 * Get Tailwind classes for ticket number based on status.
 * Uses red for open (needs work) and green for closed (resolved).
 */
export function getTicketNumberClasses(
  status: TicketStatus | undefined,
): string {
  const baseClasses = 'font-mono';
  if (status === 'closed') {
    return `${baseClasses} text-green-600 dark:text-green-400`;
  }
  // Default to open styling (red indicates needs work)
  return `${baseClasses} text-red-600 dark:text-red-400`;
}

export function getTicketLineThroughClasses(status: TicketStatus) {
  if (status === 'closed') {
    return LINE_THROUGH_CLASSES;
  }
  // Default to open styling (red indicates needs work)
  return ``;
}

/**
 * Get Tailwind classes for the complete ticket reference in editor context.
 * Includes background color to distinguish from editable text.
 */
export function getEditorTicketClasses(
  status: TicketStatus | undefined,
  isSelected: boolean = false,
): string {
  const selectionClasses = isSelected
    ? cn('ring-2 ring-blue-500 ring-offset-1')
    : '';

  if (status === 'closed') {
    return cn(
      `rounded bg-green-50 px-1 hover:bg-green-100 hover:underline dark:bg-green-950/40 dark:hover:bg-green-950/60`,
      selectionClasses,
    );
  }
  // Open: red background to indicate needs work
  return cn(
    `rounded bg-red-50 px-1 hover:bg-red-100 hover:underline dark:bg-red-950/40 dark:hover:bg-red-950/60`,
    selectionClasses,
  );
}

/**
 * Get inline status indicator for autocomplete dropdown.
 * Uses subtle text instead of badges to save space.
 */
export function getStatusIndicatorClasses(
  status: TicketStatus | undefined,
): string {
  if (status === 'closed') {
    return 'text-xs text-green-600 dark:text-green-400';
  }
  return 'text-xs text-red-600 dark:text-red-400';
}
